import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft, Loader2 } from "lucide-react";
import { resetPassword, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = new URLSearchParams(location.search).get("token") || "";
    setToken(t);
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing reset token");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await resetPassword({ token, password });
      await refresh();
      toast.success("Password updated");
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div
        data-testid="reset-card"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0F1117] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mb-6 font-mono uppercase tracking-widest"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to login
        </Link>
        <h1 className="font-serif text-3xl text-white font-bold mb-6">Choose a new password</h1>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">
              New password
            </Label>
            <div className="mt-1 relative">
              <Lock className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="reset-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="pl-9 bg-[#07080A] border-white/10 h-11"
                required
                minLength={8}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">
              Confirm password
            </Label>
            <div className="mt-1 relative">
              <Lock className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="reset-confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat your password"
                className="pl-9 bg-[#07080A] border-white/10 h-11"
                required
                minLength={8}
              />
            </div>
          </div>
          {error && (
            <div
              data-testid="reset-error"
              className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-4 py-3"
            >
              {error}
            </div>
          )}
          <Button
            data-testid="reset-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
